// OTAS-01 operator view. Admin only. Reads the live reference book written by
// the scheduled cycle. Nothing on this page starts a cycle, opens a trade,
// publishes or notifies.

import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getOtasStatus, type OtasStatus } from "@/lib/otas-status.functions";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const Route = createFileRoute("/_authenticated/admin/otas")({
  component: OtasStatusPage,
  head: () => ({
    meta: [
      { title: "H4 SMA 50 Dynamic Allocation + | XAU Pro Academy" },
      { name: "description", content: "Admin view of the OTAS-01 live engine running H4 SMA 50 dynamic allocation +, cycle receipts and readiness." },
      { property: "og:title", content: "H4 SMA 50 Dynamic Allocation +" },
      { property: "og:description", content: "Cycle state, readiness, grades, candidates and reference trades for the live OTAS-01 engine." },

      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

const utc = (s: string | number | null | undefined) => {
  if (s === null || s === undefined) return "n/a";
  const d = typeof s === "number" ? new Date(s * 1000) : new Date(s);
  return `${d.toISOString().slice(0, 16).replace("T", " ")} UTC`;
};
const local = (s: string | null | undefined) => (s ? new Date(s).toLocaleString() : "");
const px = (n: number | null | undefined) => (n === null || n === undefined ? "n/a" : n.toFixed(2));
const side = (d: number | null) => (d === 1 ? "LONG" : d === -1 ? "SHORT" : "n/a");

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border/60 py-1.5">
      <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="text-right font-mono text-sm text-foreground">{value}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-md border border-border bg-card p-4">
      <h2 className="mb-3 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{title}</h2>
      {children}
    </section>
  );
}

function DirectionalContext({ context }: { context: OtasStatus["directionalContext"] }) {
  const title = context.side === "LONG"
    ? "Looking for LONG setups"
    : context.side === "SHORT"
      ? "Looking for SHORT setups"
      : context.side === "NEUTRAL"
        ? "No aligned side"
        : "Waiting for market data";
  const tone = context.side === "LONG"
    ? "border-emerald/40 bg-emerald-muted"
    : context.side === "SHORT"
      ? "border-destructive/40 bg-destructive/5"
      : "border-border bg-card";
  const marker = context.side === "LONG"
    ? "bg-emerald"
    : context.side === "SHORT"
      ? "bg-destructive"
      : "bg-muted-foreground";

  return (
    <section className={`rounded-md border p-4 ${tone}`}>
      <div className="flex items-start gap-3">
        <span className={`mt-1.5 size-2 shrink-0 rounded-full ${marker}`} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Current acceptance context</p>
          <h2 className="mt-1 text-lg font-semibold text-foreground">{title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{context.reason}</p>
        </div>
      </div>
      <div className="mt-4 grid gap-x-6 sm:grid-cols-2 lg:grid-cols-4">
        <Row label="latest quote" value={px(context.quote)} />
        <Row label="H4 SMA 50" value={px(context.sma50)} />
        <Row label="weekly VWAP" value={px(context.weeklyVwap)} />
        <Row label="observed" value={utc(context.observedAt)} />
      </div>
      <p className="mt-3 border-t border-border/60 pt-3 text-xs text-muted-foreground">
        Directional eligibility only. Structure, pullback, timing, allocation and capacity must still qualify before entry.
      </p>
    </section>
  );
}

function Summary({ s }: { s: OtasStatus["summary"] }) {
  const cols = ["long", "short", "total"] as const;
  const rows: Array<[string, (b: OtasStatus["summary"]["total"]) => string]> = [
    ["closed", (b) => String(b.closed)],
    ["open", (b) => String(b.open)],
    ["unknown", (b) => String(b.unknown)],
    ["open after tp1", (b) => String(b.partialTp1)],
    ["gross usd/oz, all legs", (b) => b.grossUsdPerOz.toFixed(2)],
    ["net usd/oz closed, fixed cost (assumed)", (b) => b.netFixedUsdPerOz.toFixed(2)],
    ["net usd/oz closed, stress cost (assumed)", (b) => b.netStressUsdPerOz.toFixed(2)],
  ];
  return (
    <table className="w-full font-mono text-xs">
      <thead>
        <tr className="text-muted-foreground">
          <th className="py-1 text-left font-normal uppercase">per original oz</th>
          {cols.map((c) => <th key={c} className="py-1 text-right font-normal uppercase">{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, f]) => (
          <tr key={label} className="border-t border-border/60">
            <td className="py-1 text-muted-foreground">{label}</td>
            {cols.map((c) => <td key={c} className="py-1 text-right text-foreground">{f(s[c])}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function OtasStatusPage() {
  const { isAdmin, isLoading: roleLoading } = useIsAdmin();
  const fetchStatus = useServerFn(getOtasStatus);
  const { data, isLoading, error } = useQuery({
    queryKey: ["otas-status"],
    queryFn: () => fetchStatus({}),
    enabled: isAdmin,
    refetchInterval: 30_000,
  });

  if (!roleLoading && !isAdmin) {
    return <p className="mx-auto max-w-3xl px-4 py-8 font-mono text-sm text-muted-foreground">Restricted to administrators.</p>;
  }

  const fresh = data?.readiness.m5Latest
    ? Date.now() - data.readiness.m5Latest * 1000 < 15 * 60_000
    : false;
  const openTrades = data?.trades.filter((t) => !t.closedAt) ?? [];
  const closedTrades = data?.trades.filter((t) => t.closedAt) ?? [];

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-8">
      <header>
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">OTAS-01 operator desk · admin only</p>
        <h1 className="text-xl font-semibold text-foreground">Live engine</h1>
        <p className="mt-1 text-xs text-muted-foreground">
          Reference records inside Orion. Not broker fills. Nothing on this page opens trades or sends notifications.
        </p>
      </header>

      {(isLoading || roleLoading) && <p className="font-mono text-sm text-muted-foreground">Reading reference book.</p>}
      {error && <p className="font-mono text-sm text-destructive">Reference book unavailable.</p>}

      {data && (
        <>
          <DirectionalContext context={data.directionalContext} />

          <div className="grid gap-3 sm:grid-cols-3">
            <HealthTile
              label="Engine"
              value={data.runtime?.state ?? "No cycle yet"}
              note={`Last cycle ${utc(data.runtime?.lastCycleAt)}`}
              ok={Boolean(data.runtime)}
            />
            <HealthTile
              label="Market data"
              value={fresh ? "Fresh" : "Stale or closed"}
              note={`Last M5 ${utc(data.readiness.m5Latest)}`}
              ok={fresh}
            />
            <HealthTile
              label="Open positions"
              value={`${data.slotsUsed} of 2 slots`}
              note={openTrades.length ? `${openTrades.length} reference trade open` : "Book is flat"}
              ok
            />
          </div>

          <Tabs defaultValue="overview">
            <TabsList className="w-full justify-start overflow-x-auto">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="trades">Trades</TabsTrigger>
              <TabsTrigger value="candidates">Candidates</TabsTrigger>
              <TabsTrigger value="policy">Policy</TabsTrigger>
              <TabsTrigger value="telemetry">Telemetry</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4 space-y-6">
              <Section title="Open reference trades">
                {openTrades.length === 0
                  ? <p className="text-sm text-muted-foreground">No open position. The engine is waiting for a qualified setup.</p>
                  : openTrades.map((t) => <TradeCard key={t.tradeId} t={t} />)}
              </Section>
              <Section title="Results (live only)">
                <Summary s={data.summary} />
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Cost scenarios are assumptions. Illustrative 100 oz = values x 100.
                </p>
              </Section>
            </TabsContent>

            <TabsContent value="trades" className="mt-4">
              <Section title="Closed reference trades">
                {closedTrades.length === 0
                  ? <p className="text-sm text-muted-foreground">No closed reference trade since T0.</p>
                  : closedTrades.map((t) => <TradeCard key={t.tradeId} t={t} />)}
              </Section>
            </TabsContent>

            <TabsContent value="candidates" className="mt-4">
              <Section title="Setups evaluated">
                {data.candidates.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No candidate reached a decision since T0.</p>
                ) : (
                  <div className="space-y-2">
                    {data.candidates.map((c) => {
                      const label = c.vwapRejected ? "VWAP mismatch" : c.outcome;
                      const accepted = !c.vwapRejected && /OPEN|ACCEPT|COMMIT/i.test(String(c.outcome));
                      return (
                        <div key={c.key} className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 py-2">
                          <div className="flex items-center gap-2">
                            <span className={`font-mono text-xs font-semibold ${c.direction === 1 ? "text-emerald" : "text-destructive"}`}>{side(c.direction)}</span>
                            <span className="font-mono text-xs text-muted-foreground">{utc(c.decisionTime)}</span>
                            {c.grade && <span className="font-mono text-[10px] text-gold">{c.grade === "A_PLUS" ? "A+" : c.grade}</span>}
                          </div>
                          <span className={`rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${accepted ? "border-emerald/30 bg-emerald-muted text-emerald" : c.vwapRejected ? "border-gold/30 bg-gold-muted text-gold" : "border-border/60 text-muted-foreground"}`}>
                            {label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Section>
            </TabsContent>

            <TabsContent value="policy" className="mt-4 space-y-6">
              <Section title="Rules in plain terms">
                <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
                  <li>Trend side from the H4 SMA {data.spec.smaLength}, confirmed by the weekly VWAP side.</li>
                  <li>Grade A+ is always allowed. Grade A only when the book is already risk free.</li>
                  <li>Wednesdays accept A+ only. London entries start 07:30 UTC.</li>
                  <li>Stop {data.spec.sl}, TP1 {data.spec.tp1} then break even, TP2 {data.spec.tp2A} for A and {data.spec.tp2APlus} for A+.</li>
                </ul>
              </Section>
              <Section title="Gates">
                <div className="grid gap-x-6 md:grid-cols-2">
                  {Object.entries(data.gates).map(([g, on]) => <Row key={g} label={g} value={on ? "ON" : "OFF"} />)}
                </div>
              </Section>
            </TabsContent>

            <TabsContent value="telemetry" className="mt-4 space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <Section title="Cycle state">
                  <Row label="state" value={data.runtime?.state ?? "NO CYCLE YET"} />
                  <Row label="reason" value={data.runtime?.reason ?? "none"} />
                  <Row label="last cycle, local" value={local(data.runtime?.lastCycleAt)} />
                  <Row label="t0" value={utc(data.runtime?.t0)} />
                  <Row label="authority" value={data.policy.authority || "n/a"} />
                  {data.storeError && <p className="mt-2 text-xs text-destructive">{data.storeError}</p>}
                </Section>
                <Section title="Readiness">
                  <Row label="M5 rows stored" value={String(data.readiness.m5Count)} />
                  <Row label="complete H4 closes" value={`${data.readiness.h4Count} (needs 50)`} />
                  <Row label="latest H4 open" value={utc(data.readiness.h4Latest)} />
                  <Row label="latest H1 snapshot" value={`${utc(data.readiness.h1Latest)} ${data.readiness.h1LatestUsable === null ? "" : data.readiness.h1LatestUsable ? "usable" : "not usable"}`} />
                  <Row label="last live opening" value={data.latestQuote ? `${px(data.latestQuote.open)} +${data.latestQuote.lagS}s` : "none"} />
                  <Row label="last weekly VWAP read" value={data.lastWeeklyVwap ? `${px(data.lastWeeklyVwap.value)} at ${utc(data.lastWeeklyVwap.at)}` : "none"} />
                </Section>
              </div>
              <Section title="Recent cycles">
                <table className="w-full font-mono text-xs">
                  <tbody>
                    {data.cycles.map((c) => (
                      <tr key={c.startedAt} className="border-t border-border/60">
                        <td className="py-1">{utc(c.startedAt)}</td><td>{c.state}</td><td className="text-muted-foreground">{c.reason ?? ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            </TabsContent>
          </Tabs>
        </>
      )}
      <footer className="border-t border-border/60 pt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        OTAS: Orion Tactical Allocation System · Intraday engine under Orion AI
      </footer>
    </div>
  );
}

function HealthTile({ label, value, note, ok }: { label: string; value: string; note: string; ok: boolean }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="flex items-center gap-2">
        <span className={`size-1.5 rounded-full ${ok ? "bg-emerald" : "bg-gold"}`} aria-hidden="true" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
      </div>
      <p className="mt-1.5 text-sm font-semibold text-foreground">{value}</p>
      <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">{note}</p>
    </div>
  );
}

function TradeCard({ t }: { t: OtasStatus["trades"][number] }) {
  return (
    <div className="mb-3 rounded border border-border/60 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className={`font-mono text-sm font-semibold ${t.direction === 1 ? "text-emerald" : "text-destructive"}`}>{side(t.direction)}</span>
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{t.status}</span>
      </div>
      <div className="grid gap-x-6 sm:grid-cols-2">
        <Row label="entry" value={`${px(t.entryQuote)} · ${utc(t.entryTime)}`} />
        <Row label="stop / tp1 / tp2" value={`${px(t.stop)} / ${px(t.tp1)} / ${px(t.tp2)}`} />
        <Row label="current stop" value={`${px(t.activeStop)} · ${(t.remaining * 100).toFixed(0)}% left`} />
        <Row label="result usd/oz" value={t.grossUsdPerOz.toFixed(2)} />
      </div>
    </div>
  );
}
